import { cn } from '@/lib/cn';
import Logo from 'images/squaredup-logo-full.svg?react';
import { NavLink } from 'react-router-dom';
import { ThemeOptions } from 'ui/hooks/useTheme';

const coverSizes = [1286, 1988, 2571, 3840];

const coverSrcSet = (variant: string, extension: string) =>
    coverSizes.map((size) => `/product_covers/${variant}_product_${size}.${extension} ${size}w`).join(', ');

const COVER_SIZES_ATTRIBUTE = '(min-width: 1024px) 50vw, 35vw';

type CoverVariant = { variant: string; media?: string };

/**
 * An explicit theme picks its own cover; only the system theme follows the operating system.
 */
const getCoverVariants = (theme: ThemeOptions): CoverVariant[] =>
    theme === ThemeOptions.system
        ? [
              { variant: 'dark', media: '(prefers-color-scheme: dark)' },
              { variant: 'light', media: '(prefers-color-scheme: light)' }
          ]
        : [{ variant: theme === ThemeOptions.light ? 'light' : 'dark' }];

/**
 * Two-column layout shared by the pages a user passes through before reaching the app.
 */
function LoginWrapper({
    children,
    className = '',
    linkLogo = true,
    theme = ThemeOptions.dark
}: {
    children: React.ReactNode;
    linkLogo?: boolean;
    className?: string;
    theme?: ThemeOptions;
}) {
    return (
        <div
            className='flex h-full upgradePage bg-backgroundSecondary text-textPrimary'
            data-theme={theme === ThemeOptions.system ? undefined : theme}
        >
            <div className='flex w-full h-full overflow-auto text-sm min-[681px]:w-[65%] lg:w-1/2 shrink-0'>
                <div
                    className={cn(
                        'flex flex-col w-full max-w-[520px] p-8 min-[561px]:p-14 mx-auto self-start',
                        className
                    )}
                >
                    {linkLogo ? (
                        <NavLink to='/' className='inline-block w-[220px] mb-[0.85rem]'>
                            <Logo className='w-full h-auto' />
                        </NavLink>
                    ) : (
                        <span className='inline-block w-[220px] mb-[0.85rem]'>
                            <Logo className='w-full h-auto' />
                        </span>
                    )}

                    {children}
                </div>
            </div>
            <picture className='flex-1 hidden min-w-0 overflow-hidden min-[681px]:block'>
                {getCoverVariants(theme).flatMap(({ variant, media }) => [
                    <source
                        key={`${variant}-avif`}
                        type='image/avif'
                        srcSet={coverSrcSet(variant, 'avif')}
                        sizes={COVER_SIZES_ATTRIBUTE}
                        media={media}
                    />,
                    <source
                        key={`${variant}-jpg`}
                        type='image/jpeg'
                        srcSet={coverSrcSet(variant, 'jpg')}
                        sizes={COVER_SIZES_ATTRIBUTE}
                        media={media}
                    />
                ])}

                <img
                    src='/product_covers/dark_product_3840.jpg'
                    alt='Squaredup Dashboard'
                    className='object-cover object-left w-full h-full max-w-none'
                />
            </picture>
        </div>
    );
}

LoginWrapper.propTypes = {};

export default LoginWrapper;
